import * as React from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Button, Typography } from '@mui/material';
import axiosInstant from '../../../api';
import { IBrandDetailsContext, PRODUCTS_PER_PAGE } from '../SingleBrandDetails';
import { WhiteBackground } from '../../../components/WhiteBackground';
import { PageTitle } from '../../../components/PageTitle';
import { IconPlus } from '../../../utils/icons';
import IconSearch from '../../../assets/svg/search-icon-grey.svg';
import { DotLottleLoader } from '../../../components/Loader/DotLottleLoader';
import { ProductsList } from './ProductsList';
import UpdateProductListModal from './UpdateProductListModal';
import { LoadMoreBtn } from '../../../components/Loader';
import { useUser } from '../../../hooks/useUser';

export function ProductsPanel() {
  const { id } = useParams();
  const { role } = useUser();

  const {
    brandProducts: prevProducts,
    getBrandProducts: refetchProducts,
    brandProductsCount,
  } = useOutletContext<IBrandDetailsContext>();

  const [currentPage, setCurrentPage] = React.useState(1);
  const [loading, setLoading] = React.useState(false);
  const [searchProducts, setSearchProducts] = React.useState('');
  const [showAddProductModal, setShowAddProductModal] = React.useState(false);

  const isSalesTeam = role === 'SALES_TEAM';

  const fetchProducts = async (page = 1) => {
    setLoading(true);
    setCurrentPage(page);
    await refetchProducts(page);
    setLoading(false);
  };

  const handleSaveChanges = async (desireIds: number[]) => {
    try {
      setLoading(true);
      if (id) {
        await axiosInstant.post(`/brands/${id}/products`, {
          desire_ids: desireIds,
        });

        toast.success('Brand updated successfully!');
        setShowAddProductModal(false);
      }
    } catch (error) {
      toast.error('Failed to update brand. Please try again.');
    } finally {
      await fetchProducts();
      setLoading(false);
    }
  };

  if (!id) return null;

  const filteredProducts = prevProducts.filter((product) =>
    product.title?.toLowerCase().includes(searchProducts.toLowerCase())
  );

  return (
    <WhiteBackground>
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <PageTitle title="Products" />
          <div
            style={{
              cursor: isSalesTeam ? 'not-allowed' : 'default',
            }}
          >
            <Button
              variant="contained"
              color="primary"
              className="gap-[2px] !py-3 px-4"
              onClick={() => setShowAddProductModal(true)}
              disabled={isSalesTeam}
              sx={{
                '&.Mui-disabled': {
                  backgroundColor: 'var(--accent)',
                  color: '#FFF',
                  opacity: 0.5,
                },
              }}
            >
              <IconPlus />
              <Typography variant="body1" className="capitalize !font-bold">
                Add Product
              </Typography>
            </Button>
          </div>
        </div>
        <div className="flex justify-center">
          <div className="w-full relative">
            <img
              className="absolute top-1/2 transform -translate-x-1/2 -translate-y-1/2 left-5"
              src={IconSearch}
              alt="search-icon"
            />
            <input
              type="text"
              className="w-[333px] h-[40px] px-[2.25rem] py-0 gap-2 bg-surface-2 text-text-2 placeholder-muted rounded-xl focus:outline-none focus:text-text-1"
              id="search-products"
              placeholder="Search"
              value={searchProducts}
              onChange={(e) => setSearchProducts(e.target.value)}
            />
          </div>
        </div>
        <ProductsList products={filteredProducts} refetch={fetchProducts} />
        {loading && prevProducts.length === 0 && <DotLottleLoader />}
        {currentPage < Math.ceil(brandProductsCount / PRODUCTS_PER_PAGE) && (
          <LoadMoreBtn onClick={() => fetchProducts(currentPage + 1)} loading={loading} />
        )}
      </div>
      {showAddProductModal && (
        <UpdateProductListModal
          products={prevProducts}
          onClose={() => setShowAddProductModal(false)}
          onSave={handleSaveChanges}
        />
      )}
    </WhiteBackground>
  );
}
